import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Offcanvas, Form, Button, Modal } from "react-bootstrap";
import { Input, Switch, Tag } from "antd";
import { showToast } from "../../../../core/data/redux/slices/ToastSlice";
import api from "../../../../core/axios/axiosInstance";
import Select from "react-select";
import { fetchDevices } from "../../../../core/data/redux/slices/AllDeviceSlice";

const SectionHeader = ({ icon, title }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "20px 0 12px", borderBottom: "2px solid #f3f4f6", paddingBottom: 8 }}>
    <span style={{ fontSize: 15 }}>{icon}</span>
    <span style={{ fontWeight: 700, fontSize: 11, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.08em" }}>{title}</span>
  </div>
);

const EditAgentOffcanvas = ({ visible, onClose, agent }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const [resetPwdOpen, setResetPwdOpen] = useState(false);
  const [resetPwdLoading, setResetPwdLoading] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // ── Assigned Extensions ──────────────────────────────────────────────────
  const buildExtList = (ag) => {
    const saved  = ag?.assignedExtensions || [];
    const pbxExt = ag?.PBXDetails?.PBX_EXTENSION_NUMBER || ag?.extensionNumber;
    const pbxTel = ag?.PBXDetails?.PBX_TELEPHONE        || ag?.telephone;
    if (pbxExt && pbxTel && !saved.some((e) => e.extensionNumber === pbxExt)) {
      return [{ extensionNumber: pbxExt, PBX_TELEPHONE: pbxTel, assignedDeviceId: ag?.PBXDetails?.assignedDeviceId || ag?.assignedDeviceId || null, pbxType: "cloud", extensionStatus: true }, ...saved];
    }
    return saved;
  };

  const [extList, setExtList]           = useState(() => buildExtList(agent));
  const [newExtNum, setNewExtNum]       = useState("");
  const [newExtTel, setNewExtTel]       = useState("");
  const [newExtDevice, setNewExtDevice] = useState(null);
  const [editingExt, setEditingExt]     = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [formData, setFormData] = useState({
    firstname:      agent.firstname || "",
    lastname:       agent.lastname  || "",
    email:          agent.email     || "",
    accountStatus:  agent?.accountStatus === "active",
    emailVerified:  !!agent.emailVerified,
    mobileVerified: !!agent.phonenumbers?.[0]?.isVerified,
  });

  const { devices, loading: devicesLoading } = useSelector((state) => state.devices);
  const deviceOptions = (devices || []).map((d) => ({ value: d.deviceId, label: d.deviceName }));

  useEffect(() => { dispatch(fetchDevices()); }, [dispatch]);

  useEffect(() => {
    setExtList(buildExtList(agent));
    setNewExtNum(""); setNewExtTel(""); setNewExtDevice(null);
  }, [agent?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInputChange = (field, value) => setFormData((prev) => ({ ...prev, [field]: value }));

  const newExtPbxType = newExtDevice
    ? ((devices || []).find((d) => String(d.deviceId) === String(newExtDevice.value))?.pbxType || "cloud")
    : null;

  const handleAddExtension = () => {
    const ext = newExtNum.trim();
    const tel = newExtTel.trim();
    if (!ext || !tel) { dispatch(showToast({ message: "Both extension number and telephone are required", variant: "danger" })); return; }
    if (!newExtDevice) { dispatch(showToast({ message: "Device is required for each extension", variant: "danger" })); return; }
    const currentStatus = extList.find((e) => e.extensionNumber === editingExt?.extensionNumber)?.extensionStatus ?? true;
    const entry = { extensionNumber: ext, PBX_TELEPHONE: tel, assignedDeviceId: newExtDevice.value, pbxType: newExtPbxType, extensionStatus: editingExt ? currentStatus : true };
    if (editingExt) {
      setExtList((prev) => prev.map((e) => e.extensionNumber === editingExt.extensionNumber ? entry : e));
      setEditingExt(null);
    } else {
      if (extList.some((e) => e.extensionNumber === ext)) { dispatch(showToast({ message: "Extension number already exists", variant: "danger" })); return; }
      setExtList((prev) => [...prev, entry]);
    }
    setNewExtNum(""); setNewExtTel(""); setNewExtDevice(null);
  };

  const handleEditExtension = (e) => {
    setEditingExt(e);
    setNewExtNum(e.extensionNumber);
    setNewExtTel(e.PBX_TELEPHONE);
    setNewExtDevice(e.assignedDeviceId ? (deviceOptions.find((d) => d.value === e.assignedDeviceId) || null) : null);
  };

  const confirmDeleteExtension = () => { setExtList((prev) => prev.filter((e) => e.extensionNumber !== deleteTarget)); setDeleteTarget(null); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.firstname.trim()) { dispatch(showToast({ message: "First name is required", variant: "danger" })); return; }
    setLoading(true);
    try {
      const primaryExt     = extList[0];
      const extensionStatus = extList.length > 0 && extList.some((ex) => ex.extensionStatus !== false);
      const payload = {
        userId:           agent._id,
        firstname:        formData.firstname,
        lastname:         formData.lastname,
        extensionNumber:  primaryExt?.extensionNumber  || "",
        telephone:        primaryExt?.PBX_TELEPHONE    || "",
        assignedDeviceId: primaryExt?.assignedDeviceId || null,
        status:           extensionStatus,
      };
      const response = await api.put("/superAdmin/editCompanyAdminAndAgent", payload);

      if (formData.email !== agent.email) {
        await api.put("/superAdmin/updateUserContact", { userId: agent._id, email: formData.email });
      }

      if (formData.accountStatus !== (agent?.accountStatus === "active")) {
        const ep = formData.accountStatus ? "/deleteUser/activate" : "/deleteUser/suspend";
        await api.post(ep, { user_id: agent._id });
      }

      const origEV = !!agent.emailVerified;
      const origMV = !!agent.phonenumbers?.[0]?.isVerified;
      if (formData.emailVerified !== origEV || formData.mobileVerified !== origMV) {
        await api.put("/superAdmin/updateUserVerification", {
          userId: agent._id,
          isVerified: formData.emailVerified && formData.mobileVerified,
          emailVerified: formData.emailVerified,
          mobileVerified: formData.mobileVerified,
        });
      }

      // Refresh pbxType from live devices before saving to avoid stale stored values
      const extListWithPbxType = extList.map((e) => ({
        ...e,
        pbxType: e.assignedDeviceId
          ? ((devices || []).find((d) => String(d.deviceId) === String(e.assignedDeviceId))?.pbxType || e.pbxType || "cloud")
          : (e.pbxType || "cloud"),
      }));
      await api.put(`/superAdmin/companies/${agent._id}/extensions`, { extensions: extListWithPbxType });

      if (response.data.status === "success") {
        dispatch(showToast({ message: "Agent updated successfully", variant: "success" }));
        onClose(true);
      }
    } catch (error) {
      dispatch(showToast({ message: error?.response?.data?.error || "Failed to update agent", variant: "danger" }));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 8) { dispatch(showToast({ message: "Password must be at least 8 characters", variant: "danger" })); return; }
    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])/.test(newPassword)) { dispatch(showToast({ message: "Password must contain uppercase, lowercase, number and special character (@$!%*?&#)", variant: "danger" })); return; }
    if (newPassword !== confirmPassword) { dispatch(showToast({ message: "Passwords do not match", variant: "danger" })); return; }
    setResetPwdLoading(true);
    try {
      await api.post("/superAdmin/resetUserPassword", { userId: agent._id, newPassword });
      dispatch(showToast({ message: "Password reset successfully", variant: "success" }));
      setResetPwdOpen(false); setNewPassword(""); setConfirmPassword("");
    } catch (err) {
      dispatch(showToast({ message: err?.response?.data?.error || "Failed to reset password", variant: "danger" }));
    } finally {
      setResetPwdLoading(false);
    }
  };

  return (
    <>
      <Offcanvas show={visible} onHide={onClose} placement="end" style={{ width: "520px" }}>
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Edit Agent — {agent.firstname ? `${agent.firstname} ${agent.lastname}` : "N/A"}</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Form onSubmit={handleSubmit}>

            <SectionHeader icon="👤" title="Profile Information" />

            <div className="row mb-3">
              <div className="col-6">
                <Form.Group>
                  <Form.Label>First Name <span className="text-danger">*</span></Form.Label>
                  <Input placeholder="First name" value={formData.firstname} onChange={(e) => handleInputChange("firstname", e.target.value)} />
                </Form.Group>
              </div>
              <div className="col-6">
                <Form.Group>
                  <Form.Label>Last Name</Form.Label>
                  <Input placeholder="Last name" value={formData.lastname} onChange={(e) => handleInputChange("lastname", e.target.value)} />
                </Form.Group>
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Email <span className="text-danger">*</span></Form.Label>
              <Input type="email" placeholder="Enter email" value={formData.email} onChange={(e) => handleInputChange("email", e.target.value)} required />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Verification</Form.Label>
              <div className="d-flex flex-column gap-2 mt-1">
                <Form.Check type="checkbox" id="agent-email-verified" label="Email verified" checked={formData.emailVerified} onChange={(e) => handleInputChange("emailVerified", e.target.checked)} />
                <Form.Check type="checkbox" id="agent-mobile-verified" label="Mobile verified" checked={formData.mobileVerified} onChange={(e) => handleInputChange("mobileVerified", e.target.checked)} />
              </div>
            </Form.Group>

            {/* ════════════════ Assigned Extensions ═══════════════ */}
            <SectionHeader icon="📞" title="Assigned Extensions" />

            {extList.length > 0 ? (
              <div className="mb-3">
                {extList.map((e) => {
                  const resolvedPbxType = e.assignedDeviceId
                    ? ((devices || []).find((d) => String(d.deviceId) === String(e.assignedDeviceId))?.pbxType || e.pbxType || "cloud")
                    : (e.pbxType || "cloud");
                  return (
                    <div key={e.extensionNumber} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 12px", marginBottom: 6, background: editingExt?.extensionNumber === e.extensionNumber ? "#eff6ff" : "#f9fafb", border: `1px solid ${editingExt?.extensionNumber === e.extensionNumber ? "#93c5fd" : "#e5e7eb"}`, borderRadius: 6 }}>
                      <span style={{ fontSize: 13 }}>
                        <strong>Ext {e.extensionNumber}</strong>
                        <span style={{ color: "#6b7280", marginLeft: 8 }}>{e.PBX_TELEPHONE}</span>
                        {e.assignedDeviceId && (
                          <span style={{ color: "#9333ea", marginLeft: 6, fontSize: 11 }}>
                            [{deviceOptions.find((d) => d.value === e.assignedDeviceId)?.label || "Device"}]
                          </span>
                        )}
                        <Tag
                          color={devicesLoading ? "default" : (resolvedPbxType === "local" ? "orange" : "blue")}
                          style={{ fontSize: 10, marginLeft: 6, textTransform: "capitalize" }}
                        >
                          {devicesLoading ? "…" : resolvedPbxType}
                        </Tag>
                      </span>
                      <div className="d-flex gap-2 align-items-center">
                        <Switch size="small" checked={e.extensionStatus !== false} onChange={(checked) => setExtList((prev) => prev.map((ex) => ex.extensionNumber === e.extensionNumber ? { ...ex, extensionStatus: checked } : ex))} checkedChildren="Active" unCheckedChildren="Off" />
                        <button type="button" onClick={() => handleEditExtension(e)} style={{ background: "none", border: "none", cursor: "pointer", color: "#3b82f6", fontSize: 13, padding: "2px 6px" }}>✏️</button>
                        <button type="button" onClick={() => setDeleteTarget(e.extensionNumber)} style={{ background: "none", border: "none", cursor: "pointer", color: "#ef4444", fontSize: 16, lineHeight: 1, padding: "0 4px" }}>×</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-muted small mb-3">No extensions assigned yet.</div>
            )}

            <div style={{ background: editingExt ? "#eff6ff" : "#f9fafb", border: `1px solid ${editingExt ? "#93c5fd" : "#e5e7eb"}`, borderRadius: 8, padding: "10px 12px", marginBottom: 12 }}>
              <div className="d-flex gap-2 mb-2">
                <Input placeholder="Ext no. (e.g. 1010)" value={newExtNum} onChange={(e) => setNewExtNum(e.target.value)} style={{ flex: 1 }} />
                <Input placeholder="Phone no. (e.g. 971585990233)" value={newExtTel} onChange={(e) => setNewExtTel(e.target.value)} style={{ flex: 1.6 }} />
              </div>
              <div className="d-flex gap-2 mb-2 align-items-center">
                <div style={{ flex: 1 }}>
                  <Select options={deviceOptions} value={newExtDevice} onChange={setNewExtDevice} placeholder="Select device *" isSearchable />
                </div>
                {newExtPbxType && (
                  <Tag color={newExtPbxType === "local" ? "orange" : "blue"} style={{ fontSize: 11, textTransform: "capitalize", margin: 0 }}>{newExtPbxType}</Tag>
                )}
              </div>
              <div className="d-flex gap-2 justify-content-end">
                <Button type="button" variant={editingExt ? "outline-success" : "outline-primary"} size="sm" onClick={handleAddExtension} style={{ whiteSpace: "nowrap" }}>
                  {editingExt ? "Save Extension" : "+ Add Extension"}
                </Button>
                {editingExt && (
                  <Button type="button" variant="outline-secondary" size="sm" onClick={() => { setEditingExt(null); setNewExtNum(""); setNewExtTel(""); setNewExtDevice(null); }}>Cancel</Button>
                )}
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Account Status</Form.Label>
              <div>
                <Switch checked={formData.accountStatus} onChange={(checked) => handleInputChange("accountStatus", checked)} checkedChildren="Active" unCheckedChildren="Suspended" />
              </div>
              <div className="form-text text-muted mt-2">When suspended, user cannot login to the system.</div>
            </Form.Group>

            <SectionHeader icon="🔒" title="Security" />

            <div className="mb-4">
              <Button variant="outline-warning" size="sm" type="button" onClick={() => { setNewPassword(""); setConfirmPassword(""); setResetPwdOpen(true); }}>
                <i className="ti ti-lock me-1" />Reset Password
              </Button>
            </div>

            <div className="d-flex justify-content-end gap-2 pt-2 border-top">
              <Button variant="secondary" type="button" onClick={onClose} disabled={loading}>Cancel</Button>
              <Button variant="primary" type="submit" disabled={loading}>{loading ? "Saving…" : "Save Changes"}</Button>
            </div>

          </Form>
        </Offcanvas.Body>
      </Offcanvas>

      <Modal show={!!deleteTarget} onHide={() => setDeleteTarget(null)} centered size="sm">
        <Modal.Header closeButton><Modal.Title style={{ fontSize: 15 }}>Remove Extension</Modal.Title></Modal.Header>
        <Modal.Body>Remove <strong>Ext {deleteTarget}</strong>?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button variant="danger" size="sm" onClick={confirmDeleteExtension}>Remove</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={resetPwdOpen} onHide={() => setResetPwdOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 16 }}><i className="ti ti-lock me-2 text-warning" />Reset Password — {agent?.email}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>New Password</Form.Label>
            <Input.Password placeholder="Minimum 8 characters" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
            <div className="form-text text-muted mt-1" style={{ fontSize: 12 }}>Min 8 chars with uppercase, lowercase, number and special character (@$!%*?&#)</div>
          </Form.Group>
          <Form.Group>
            <Form.Label>Confirm Password</Form.Label>
            <Input.Password placeholder="Re-enter new password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setResetPwdOpen(false)} disabled={resetPwdLoading}>Cancel</Button>
          <Button variant="warning" onClick={handleResetPassword} disabled={resetPwdLoading}>{resetPwdLoading ? "Resetting…" : "Reset Password"}</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default EditAgentOffcanvas;
